import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Search, ChevronDown } from "lucide-react";
import { WebViewTooltip } from "../Pages/Diary/SubHeader/WebViewTooltip";
import { WebViewPopover } from "../Pages/Diary/SubHeader/WebViewPopover";
import { EDSVenuePicker } from "../eds/EDSVenuePicker";
import "../../styles/MainMenu.css";
import { Skeleton } from "../ui/skeleton";

export interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  isActive?: boolean;
  onClick?: () => void;
  badge?: number;
  children?: MenuItem[];
  hasVenuePicker?: boolean;
  venuePickerPath?: string;
}

export interface MainMenuProps {
  items: MenuItem[];
  bottomItems?: MenuItem[]; // Separate array for bottom items
  headerContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  collapsible?: boolean;
  collapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
  onItemClick?: (item: MenuItem) => void;
  onSearchClick?: () => void;
  className?: string;
  currentVenueId?: string;
  loading?: boolean;
}

export const MainMenu: React.FC<MainMenuProps> = ({
  items,
  bottomItems = [],
  headerContent,
  footerContent,
  collapsible = true,
  collapsed = false,
  onCollapseChange,
  onItemClick,
  onSearchClick,
  className = "",
  currentVenueId,
  loading = false
}) => {
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [openPopover, setOpenPopover] = useState<string | null>(null);

  useEffect(() => {
    setIsCollapsed(collapsed);
  }, [collapsed]);

  const handleCollapseToggle = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    onCollapseChange?.(newCollapsed);
  };

  const handleItemClick = (item: MenuItem) => {
    const hasChildren =
      item.hasVenuePicker || (item.children && item.children.length > 0);

    // Handle expandable items (venue picker or children)
    if (hasChildren) {
      if (isCollapsed) {
        // When collapsed, toggle popover
        setOpenPopover(openPopover === item.id ? null : item.id);
      } else {
        // When expanded, toggle expansion
        const newExpandedItems = new Set(expandedItems);
        if (newExpandedItems.has(item.id)) {
          newExpandedItems.delete(item.id);
        } else {
          newExpandedItems.add(item.id);
        }
        setExpandedItems(newExpandedItems);
      }
    } else {
      item.onClick?.();
      onItemClick?.(item);
    }
  };

  const renderMenuItem = (item: MenuItem) => {
    if (loading) {
      return (
        <Skeleton
          key={item.id}
          style={{ width: "100%", height: "34px", marginBottom: "6px" }}
        />
      );
    }
    const isExpanded = expandedItems.has(item.id);
    const hasChildren =
      item.hasVenuePicker || (item.children && item.children.length > 0);

    const menuButton = (
      <button
        className={`main-menu-item ${item.isActive ? "active" : ""}`}
        onClick={() => handleItemClick(item)}
        disabled={!item.onClick && !onItemClick && !hasChildren}
      >
        <div className="main-menu-item-icon">{item.icon}</div>
        {!isCollapsed && (
          <>
            <span className="main-menu-item-label">
              {item.label}
              {item.badge && (
                <span className="main-menu-item-badge">{item.badge}</span>
              )}
            </span>
            {hasChildren && (
              <ChevronDown
                className={`main-menu-chevron ${isExpanded ? "expanded" : ""}`}
                size={16}
              />
            )}
          </>
        )}
      </button>
    );

    // Wrap with tooltip or popover when collapsed
    if (isCollapsed) {
      // Wrap with tooltip
      const tooltipWrappedButton = (
        <WebViewTooltip
          key={item.id}
          content={item.label}
          side="right"
          sideOffset={8}
        >
          {menuButton}
        </WebViewTooltip>
      );

      // If this item has a popover open, wrap it with the appropriate popover
      if (openPopover === item.id) {
        if (item.hasVenuePicker) {
          return (
            <WebViewPopover
              key={`${item.id}-popover`}
              open={true}
              onOpenChange={(open) => setOpenPopover(open ? item.id : null)}
              side="right"
              sideOffset={8}
              content={
                <>
                  <div className="main-menu-venue-popover-header">
                    <h3 className="main-menu-venue-popover-title">
                      {item.label}
                    </h3>
                  </div>
                  <EDSVenuePicker
                    parentItem={{
                      text: item.label,
                      icon: "Calendar",
                      link: item.venuePickerPath ?? "",
                      venue_picker: {
                        path: item.venuePickerPath ?? ""
                      }
                    }}
                    currentVenueId={currentVenueId}
                    setExpandedItems={setExpandedItems}
                  />
                </>
              }
            >
              {tooltipWrappedButton}
            </WebViewPopover>
          );
        } else if (hasChildren) {
          return (
            <WebViewPopover
              key={`${item.id}-popover`}
              open={true}
              onOpenChange={(open) => setOpenPopover(open ? item.id : null)}
              side="right"
              sideOffset={8}
              content={
                <div className="main-menu-children-popover">
                  <div className="main-menu-children-popover-header">
                    <h3 className="main-menu-children-popover-title">
                      {item.label}
                    </h3>
                  </div>
                  <div className="main-menu-children-popover-content">
                    {item.children?.map((child) => (
                      <button
                        key={child.id}
                        className="main-menu-child-item"
                        onClick={() => {
                          child.onClick?.();
                          onItemClick?.(child);
                          setOpenPopover(null); // Close popover after selection
                        }}
                      >
                        <div className="main-menu-child-item-icon">
                          {child.icon}
                        </div>
                        <span className="main-menu-child-item-label">
                          {child.label}
                          {child.badge && (
                            <span className="main-menu-child-item-badge">
                              {child.badge}
                            </span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              }
            >
              {tooltipWrappedButton}
            </WebViewPopover>
          );
        }
      }

      return tooltipWrappedButton;
    }

    return (
      <React.Fragment key={item.id}>
        {menuButton}
        {isExpanded && item.hasVenuePicker && (
          <div className="main-menu-venue-picker">
            <EDSVenuePicker
              parentItem={{
                text: item.label,
                icon: "Calendar", // Default icon since we can't convert ReactNode to string
                link: item.venuePickerPath ?? "",
                venue_picker: {
                  path: item.venuePickerPath ?? ""
                }
              }}
              currentVenueId={currentVenueId}
              setExpandedItems={setExpandedItems}
            />
          </div>
        )}
        {isExpanded && item.children && item.children.length > 0 && (
          <div className="main-menu-children">
            {item.children.map((child) => renderMenuItem(child))}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className={`main-menu-container ${className}`}>
      <div
        className={`main-menu-sidebar ${isCollapsed ? "collapsed" : "expanded"}`}
      >
        {/* Header */}
        <div className="main-menu-header">
          <div className="main-menu-header-content">
            <div className="main-menu-toggle-section">
              {collapsible && (
                <button
                  className="main-menu-toggle-button"
                  onClick={handleCollapseToggle}
                  aria-label={isCollapsed ? "Expand menu" : "Collapse menu"}
                >
                  {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
                </button>
              )}
              {!isCollapsed && (
                <span className="main-menu-title">
                  {headerContent || "Navigation"}
                </span>
              )}
            </div>
            {onSearchClick && (
              <button
                className="main-menu-search-button"
                onClick={onSearchClick}
                aria-label="Search"
              >
                <Search />
              </button>
            )}
          </div>
        </div>

        {/* Top Menu Items */}
        <div className="main-menu-items">
          {items.map((item) => renderMenuItem(item))}
        </div>

        {/* Bottom Menu Items */}
        {bottomItems.length > 0 && (
          <div className="main-menu-bottom-items">
            {bottomItems.map((item) => renderMenuItem(item))}
          </div>
        )}

        {/* Footer */}
        {footerContent && (
          <div className="main-menu-footer">{footerContent}</div>
        )}
      </div>
    </div>
  );
};

export default MainMenu;
